import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import LanguageSelector from '@/components/LanguageSelector';
import { Layers, Sun, Moon, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSubmitted(true);
      
      // In development/preview, we show the reset token for testing
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
      
      toast.success(t('auth.resetLinkSent'));
    } catch (error) {
      const message = error.response?.data?.detail || t('auth.resetError');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12">
        <div className="w-full max-w-md mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-12">
            <Link to="/auth" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">{t('auth.backToLogin')}</span>
            </Link>
            <div className="flex items-center gap-2">
              <LanguageSelector variant="compact" />
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="forgot-theme-toggle">
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-foreground rounded-xl flex items-center justify-center">
              <Layers className="w-6 h-6 text-background" />
            </div>
            <span className="font-semibold text-2xl tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MockupPro
            </span>
          </div>

          {!submitted ? (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="forgot-heading"
              >
                {t('auth.forgotPassword')}
              </h1>
              <p className="text-muted-foreground mb-8">
                {t('auth.forgotPasswordInfo')}
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full rounded-lg" 
                  disabled={loading}
                  data-testid="forgot-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner" /> {t('auth.processing')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {t('auth.sendResetLink')}
                    </span>
                  )}
                </Button>
              </form>

              <p className="text-center text-sm text-muted-foreground mt-8">
                {t('auth.rememberPassword')}{' '}
                <Link 
                  to="/auth"
                  className="text-foreground hover:underline font-medium"
                  data-testid="back-to-login-link"
                >
                  {t('auth.signInLink')}
                </Link>
              </p>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {t('auth.checkYourEmail')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {t('auth.resetLinkSentTo')} <strong>{email}</strong>
              </p>
              
              {/* Development/Preview: Show reset link for testing */}
              {resetToken && (
                <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6 text-left">
                  <p className="text-xs text-muted-foreground mb-2">
                    <strong>Preview Mode:</strong> Use this link to reset your password
                  </p>
                  <Link 
                    to={`/reset-password?token=${resetToken}`}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
                    data-testid="reset-link"
                  >
                    Reset Password Link
                  </Link>
                </div>
              )}

              <p className="text-sm text-muted-foreground mb-6">
                {t('auth.didntReceiveEmail')}
              </p>
              
              <Button 
                variant="outline" 
                onClick={() => { setSubmitted(false); setResetToken(null); }}
                className="mr-2"
              >
                {t('auth.tryAgain')}
              </Button>
              <Link to="/auth">
                <Button variant="default">
                  {t('auth.backToLogin')}
                </Button>
              </Link>
            </motion.div>
          )}
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 bg-muted/30 items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="w-24 h-24 bg-accent rounded-3xl flex items-center justify-center mx-auto mb-8">
            <Mail className="w-12 h-12" />
          </div>
          <h2 
            className="text-2xl font-bold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {t('auth.resetRightTitle')}
          </h2>
          <p className="text-muted-foreground">
            {t('auth.resetRightSubtitle')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
